import { fetchJSON as fetchAPI } from './client';

const BASE = import.meta.env.VITE_EVENTPLATFORM_API ?? 'http://localhost:8161';

export type PlatformDashboard = {
  total_events: number;
  pending_outbox: number;
  queue_depth: number;
  dlq_count: number;
  retry_count: number;
  registry_count: number;
  publisher_count: number;
  consumer_count: number;
  throughput_per_min?: number;
  failure_rate?: number;
  recent_activity?: ActivityEntry[];
};

export type ActivityEntry = {
  id: string;
  tenant_id: string;
  action: string;
  resource: string;
  details?: string;
  actor: string;
  created_at: string;
};

export type DomainEvent = {
  id: string;
  name: string;
  version: string;
  kind: string;
  tenant_id?: string;
  organization_id?: string;
  module_id?: string;
  correlation_id?: string;
  timestamp: string;
  payload?: unknown;
};

export type RegistryEntry = {
  id: string;
  event_name: string;
  version: string;
  kind: string;
  module_id?: string;
  description?: string;
};

export type PublisherRecord = {
  id: string;
  module_id: string;
  event_name: string;
  active: boolean;
  created_at: string;
};

export type ConsumerRecord = {
  id: string;
  module_id: string;
  event_name: string;
  active: boolean;
  health: string;
  created_at: string;
};

export type SagaRecord = {
  id: string;
  name: string;
  module_id?: string;
  tenant_id: string;
  status: string;
  correlation_id?: string;
  created_at: string;
  updated_at: string;
};

export type DeadLetterRecord = {
  event_id: string;
  event_name: string;
  error: string;
  at: string;
};

function headers() {
  return { 'X-Tenant-ID': 'default', 'X-Organization-ID': 'default' };
}

export const eventPlatformApi = {
  dashboard: () => fetchAPI<PlatformDashboard>(`${BASE}/api/v1/events/dashboard`, { headers: headers() }),

  listEvents: (params?: { event_name?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.event_name) q.set('event_name', params.event_name);
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return fetchAPI<{ events: DomainEvent[] }>(`${BASE}/api/v1/events${qs ? `?${qs}` : ''}`, { headers: headers() });
  },

  getEvent: (id: string) => fetchAPI<DomainEvent>(`${BASE}/api/v1/events/${id}`, { headers: headers() }),

  stream: (limit = 50) => fetchAPI<{ events: DomainEvent[] }>(`${BASE}/api/v1/events/stream?limit=${limit}`, { headers: headers() }),

  publish: (body: { name: string; kind: string; payload: unknown; module_id?: string; async?: boolean; use_outbox?: boolean }) =>
    fetchAPI<{ published: boolean; event: DomainEvent }>(`${BASE}/api/v1/events/publish`, {
      method: 'POST', headers: headers(), body: JSON.stringify(body),
    }),

  registry: () => fetchAPI<{ registry: RegistryEntry[] }>(`${BASE}/api/v1/events/registry`, { headers: headers() }),

  catalog: () => fetchAPI<{ catalog: RegistryEntry[] }>(`${BASE}/api/v1/events/catalog`, { headers: headers() }),

  publishers: () => fetchAPI<{ publishers: PublisherRecord[] }>(`${BASE}/api/v1/events/publishers`, { headers: headers() }),

  consumers: () => fetchAPI<{ consumers: ConsumerRecord[] }>(`${BASE}/api/v1/events/consumers`, { headers: headers() }),

  sagas: () => fetchAPI<{ sagas: SagaRecord[] }>(`${BASE}/api/v1/events/sagas`, { headers: headers() }),

  deadLetter: () => fetchAPI<{ records: DeadLetterRecord[] }>(`${BASE}/api/v1/events/dead-letter`, { headers: headers() }),

  retries: () => fetchAPI<{ retries: unknown[] }>(`${BASE}/api/v1/events/retries`, { headers: headers() }),

  replay: (body: { event_id?: string; event_name?: string; limit?: number }) =>
    fetchAPI<{ replayed: number }>(`${BASE}/api/v1/events/replay`, { method: 'POST', headers: headers(), body: JSON.stringify(body) }),

  registerModule: (body: { module_id: string; published: string[]; subscribed: string[] }) =>
    fetchAPI<{ status: string }>(`${BASE}/api/v1/events/register-module`, { method: 'POST', headers: headers(), body: JSON.stringify(body) }),
};

export type EventRegistryEntry = {
  id: string;
  tenant_id?: string;
  event_name: string;
  domain: string;
  version: string;
  owner: string;
  producer: string;
  consumers?: string[];
  schema_id?: string;
  retention_policy: string;
  ordering_required: boolean;
  idempotency_required: boolean;
  security_classification: string;
  lifecycle_status: string;
  kind: string;
  registered_at: string;
};

export type SchemaRegistryEntry = {
  id: string;
  tenant_id?: string;
  event_name: string;
  version: string;
  format: string;
  schema_json: string;
  backward_compatible: boolean;
  breaking_change_detected: boolean;
  compatibility_status: string;
  deprecated: boolean;
  published_at: string;
};

export type ProducerRegistryEntry = {
  id: string;
  tenant_id?: string;
  service: string;
  event_name: string;
  version: string;
  active: boolean;
  auth_scope?: string;
  created_at: string;
};

export type ConsumerRegistryEntry = {
  id: string;
  tenant_id?: string;
  service: string;
  event_name: string;
  version: string;
  active: boolean;
  health: string;
  consumer_lag_ms?: number;
  delivery_mode: string;
  created_at: string;
};

export type RoutingRule = {
  id: string;
  name: string;
  strategy: string;
  event_pattern: string;
  target: string;
  priority: number;
  tenant_id?: string;
  domain?: string;
  enabled: boolean;
};

export type DeliveryPolicy = {
  id: string;
  name: string;
  mode: string;
  retry_max: number;
  backoff_ms: number;
  dlq_enabled: boolean;
  deduplication: boolean;
  applies_to?: string[];
  enabled: boolean;
};

export type EventLineageEntry = {
  event_name: string;
  producer: string;
  consumers?: string[];
  downstream_events?: string[];
};

export type EventMeshHealth = {
  tenant_id: string;
  event_health_score: number;
  event_throughput_per_min: number;
  consumer_lag_ms: number;
  delivery_success_rate: number;
  retry_rate: number;
  dead_letter_rate: number;
  replay_count: number;
  schema_compliance: number;
  registered_events: number;
  active_producers: number;
  active_consumers: number;
  validated_at: string;
};

export type SchemaValidationResult = {
  valid: boolean;
  compatibility_status: string;
  breaking_changes?: string[];
  consumer_impact?: string[];
  message: string;
};

export type ReplaySummary = {
  tenant_id: string;
  total_jobs: number;
  completed: number;
  pending: number;
  events_replayed: number;
};

export type EventMeshDashboard = {
  tenant_id: string;
  events: EventRegistryEntry[];
  schemas: SchemaRegistryEntry[];
  producers: ProducerRegistryEntry[];
  consumers: ConsumerRegistryEntry[];
  health: EventMeshHealth;
  routing: RoutingRule[];
  delivery_policies: DeliveryPolicy[];
  lineage: EventLineageEntry[];
  replay: ReplaySummary;
};

export const eventMeshGovernanceApi = {
  listMeshEvents: () =>
    fetchAPI<{ events: EventRegistryEntry[] }>(`${BASE}/api/v1/events/mesh/events`, { headers: headers() }).then((r) => r.events),

  listSchemas: () =>
    fetchAPI<{ schemas: SchemaRegistryEntry[] }>(`${BASE}/api/v1/events/schemas`, { headers: headers() }).then((r) => r.schemas),

  validateSchema: (body: { schema_id?: string; event_name?: string; version?: string; consumer_service?: string }) =>
    fetchAPI<SchemaValidationResult>(`${BASE}/api/v1/events/schemas/validate`, {
      method: 'POST', headers: headers(), body: JSON.stringify(body),
    }),

  listProducers: () =>
    fetchAPI<{ producers: ProducerRegistryEntry[] }>(`${BASE}/api/v1/events/producers`, { headers: headers() }).then((r) => r.producers),

  listMeshConsumers: () =>
    fetchAPI<{ consumers: ConsumerRegistryEntry[] }>(`${BASE}/api/v1/events/mesh/consumers`, { headers: headers() }).then((r) => r.consumers),

  routing: () =>
    fetchAPI<{ routing_rules: RoutingRule[] }>(`${BASE}/api/v1/events/mesh/routing`, { headers: headers() }).then((r) => r.routing_rules),

  meshHealth: () =>
    fetchAPI<EventMeshHealth>(`${BASE}/api/v1/events/mesh/health`, { headers: headers() }),

  lineage: () =>
    fetchAPI<{ lineage: EventLineageEntry[] }>(`${BASE}/api/v1/events/mesh/lineage`, { headers: headers() }).then((r) => r.lineage),

  replaySummary: () =>
    fetchAPI<{ summary: ReplaySummary }>(`${BASE}/api/v1/events/replay`, { headers: headers() }).then((r) => r.summary),

  dashboard: () =>
    fetchAPI<EventMeshDashboard>(`${BASE}/api/v1/events/mesh/dashboard`, { headers: headers() }),
};
